import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/shadcn/card';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { Alert, AlertTitle, AlertDescription } from '@/components/shadcn/alert';
import { AlertCircle, X, PlusCircle } from 'lucide-react';
import {
  fetchSlot1Options,
  createTrialHold,
  // fetchSlot2Options, — superseded: slot 2 is derived server-side (one-slot flow)
  releaseTrialHold,
  errorCodeFrom,
} from '../../../services/trialBookingService';

const YEAR_GROUPS = ['Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'];

const sortByTime = (a, b) => a.time.localeCompare(b.time);

// Options carry concrete bookable dates; the picker is date-first. Keys are YYYY-MM-DD
// (sort chronologically), values are the times available on that date. Each entry keeps
// slot2Time (the derived +30-minute free-trial half) for the confirmation panel.
const groupOptionsByDate = (options) => {
  const groups = {};
  for (const opt of options) {
    for (const date of opt.dates || []) {
      if (!groups[date]) groups[date] = [];
      groups[date].push({ scheduleId: opt.scheduleId, time: opt.time, slot2Time: opt.slot2Time });
    }
  }
  Object.keys(groups).forEach((d) => groups[d].sort(sortByTime));
  return groups;
};

// "2026-07-16" → "Thu 16 Jul"
const formatDateLabel = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

// "17:00" + 30 → "17:30". Display-only; the server owns the real pairing.
const addMinutesHHmm = (time, minutes) => {
  const [h, m] = (time || '').split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const makeEmptyChild = (seed = {}) => ({
  name: seed.name || '',
  yearGroup: seed.yearGroup || '',
  dateOfBirth: seed.dateOfBirth || '',
  gender: seed.gender || '',
  slot1Date: seed.slot1Date || '',
  slot1Time: '',
  slot1ScheduleId: seed.slot1ScheduleId || null,
  // Derived free-trial start time ("HH:mm") of the chosen option — display only.
  slot2Time: '',
  holdId: seed.holdId || null,
  holdLoading: false,
  holdError: null,
  // Superseded by the one-slot flow (slot 2 derived server-side):
  // slot2Options: [], slot2Loading: false, slot2Date: '', slot2ScheduleId: null,
});

function ChildDetails({
  onComplete,
  childDetails,
  leadYearGroupSeed = '',
  maxChildren = 4,
  continueLabel = 'Begin Free Trial',
}) {
  const [children, setChildren] = useState(() => {
    if (childDetails && childDetails.length > 0) {
      return childDetails.map((c) => makeEmptyChild(c));
    }
    // First visit (no saved progress): pre-fill child 1's year group from the lead form
    // when present and valid. Falls back to the empty picker otherwise. Child 2+ added
    // later via handleAddChild get no seed.
    const seedYear = YEAR_GROUPS.includes(leadYearGroupSeed) ? leadYearGroupSeed : '';
    return [makeEmptyChild({ yearGroup: seedYear })];
  });

  const [slot1Options, setSlot1Options] = useState([]);
  const [slot1Groups, setSlot1Groups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const childrenRef = useRef(children);
  useEffect(() => {
    childrenRef.current = children;
  }, [children]);

  const consumedRef = useRef(false);

  const updateChild = useCallback((idx, patch) => {
    setChildren((prev) => {
      const next = [...prev];
      if (!next[idx]) return prev;
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }, []);

  const validateDateOfBirth = (date) => {
    const today = new Date();
    const inputDate = new Date(date);
    return inputDate < today;
  };

  // Fetch slot options on mount.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const opts = await fetchSlot1Options();
        if (cancelled) return;
        const list = Array.isArray(opts) ? opts : [];
        setSlot1Options(list);
        setSlot1Groups(groupOptionsByDate(list));
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load slot options', err);
        setError('Could not load available slots. Please refresh and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const acquireHold = useCallback(
    async (idx, slot1ScheduleId, slot1DateUK) => {
      updateChild(idx, { holdLoading: true, holdError: null });
      try {
        const response = await createTrialHold(slot1ScheduleId, slot1DateUK);
        updateChild(idx, {
          holdId: response.holdId,
          holdLoading: false,
          holdError: null,
        });
      } catch (err) {
        const code = errorCodeFrom(err);
        const message =
          code === 'NO_CANDIDATE_CLASSROOMS'
            ? 'That time just filled. Please pick another.'
            : 'Could not reserve that time. Please try again.';
        updateChild(idx, {
          holdLoading: false,
          holdError: message,
          slot1Date: '',
          slot1Time: '',
          slot1ScheduleId: null,
          slot2Time: '',
          holdId: null,
        });
      }
    },
    [updateChild],
  );

  // Re-acquire holds for any children whose progress already had a slot1ScheduleId.
  // Runs once after slot1Options resolves; the inner guard prevents re-fire.
  // Any persisted holdId from progress is released best-effort before creating a fresh
  // one, so the user always starts the schedule step on a current hold.
  const reacquiredRef = useRef(new Set());
  useEffect(() => {
    if (loading || slot1Options.length === 0) return;
    children.forEach((child, idx) => {
      if (!child.slot1ScheduleId || child.holdLoading || reacquiredRef.current.has(idx)) {
        return;
      }
      reacquiredRef.current.add(idx);

      const opt = slot1Options.find(
        (o) =>
          o.scheduleId === child.slot1ScheduleId &&
          (o.dates || []).includes(child.slot1Date),
      );
      if (!opt) {
        // Persisted slot (or its date) no longer viable — clear it.
        updateChild(idx, {
          slot1Date: '',
          slot1ScheduleId: null,
          slot2Time: '',
          holdId: null,
          holdError: 'Your previously chosen time is no longer available. Please pick again.',
        });
        return;
      }

      updateChild(idx, { slot1Time: opt.time, slot2Time: opt.slot2Time || addMinutesHHmm(opt.time, 30) });

      if (child.holdId) {
        releaseTrialHold(child.holdId);
      }
      acquireHold(idx, child.slot1ScheduleId, child.slot1Date);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, slot1Options]);

  // Cleanup: release all open holds on unmount + beforeunload.
  useEffect(() => {
    const releaseAll = () => {
      if (consumedRef.current) return;
      childrenRef.current.forEach((c) => {
        if (c.holdId) releaseTrialHold(c.holdId);
      });
    };
    window.addEventListener('beforeunload', releaseAll);
    return () => {
      window.removeEventListener('beforeunload', releaseAll);
      releaseAll();
    };
  }, []);

  const handleAddChild = () => {
    if (children.length < maxChildren) {
      setChildren([...children, makeEmptyChild()]);
    }
  };

  const handleRemoveChild = (indexToRemove) => {
    const removed = children[indexToRemove];
    if (removed?.holdId) releaseTrialHold(removed.holdId);
    setChildren(children.filter((_, idx) => idx !== indexToRemove));
    reacquiredRef.current.delete(indexToRemove);
  };

  const handleChildChange = (index, field, value) => {
    if (field === 'dateOfBirth' && value && !validateDateOfBirth(value)) {
      setError('Date of birth must be in the past');
      return;
    }
    updateChild(index, { [field]: value });
    setError('');
  };

  const handleSlot1DateChange = (idx, date) => {
    const child = children[idx];
    if (child.holdId) releaseTrialHold(child.holdId);
    updateChild(idx, {
      slot1Date: date,
      slot1Time: '',
      slot1ScheduleId: null,
      slot2Time: '',
      holdId: null,
      holdError: null,
    });
  };

  const handleSlot1TimeChange = (idx, time) => {
    const child = children[idx];
    if (!child.slot1Date) return;
    const slot = (slot1Groups[child.slot1Date] || []).find((s) => s.time === time);
    if (!slot) return;
    if (child.holdId) releaseTrialHold(child.holdId);
    updateChild(idx, {
      slot1Time: time,
      slot1ScheduleId: slot.scheduleId,
      slot2Time: slot.slot2Time || addMinutesHHmm(time, 30),
      holdId: null,
      holdError: null,
    });
    acquireHold(idx, slot.scheduleId, child.slot1Date);
  };

  // ── Superseded by the one-slot flow (no slot-2 picker): ──
  // const slot2GroupsFor = (child) => groupOptionsByDate(child.slot2Options);
  // const handleSlot2DateChange = (idx, date) => {
  //   updateChild(idx, { slot2Date: date, slot2Time: '', slot2ScheduleId: null });
  // };
  // const handleSlot2TimeChange = (idx, time) => {
  //   const child = children[idx];
  //   if (!child.slot2Date) return;
  //   const slot = (slot2GroupsFor(child)[child.slot2Date] || []).find((s) => s.time === time);
  //   if (!slot) return;
  //   updateChild(idx, { slot2Time: time, slot2ScheduleId: slot.scheduleId });
  // };

  const isChildComplete = (child) =>
    !!(
      child.name &&
      child.yearGroup &&
      child.gender &&
      child.dateOfBirth &&
      child.slot1Date &&
      child.slot1ScheduleId &&
      child.holdId &&
      !child.holdLoading
    );

  const handleContinue = async () => {
    setError('');
    if (!children.every(isChildComplete)) {
      setError('Please complete every field for each child before continuing.');
      return;
    }
    setSubmitting(true);
    consumedRef.current = true; // suppress hold cleanup while we finalize / navigate
    try {
      await onComplete({
        children: children.map((child) => ({
          name: child.name,
          yearGroup: child.yearGroup,
          dateOfBirth: child.dateOfBirth,
          gender: child.gender || null,
          slot1ScheduleId: child.slot1ScheduleId,
          slot1Date: child.slot1Date,
          holdId: child.holdId,
          scheduledLessons: [],
          // slot2ScheduleId / slot2Date are no longer sent — the server derives the
          // +30-minute companion from the hold.
        })),
      });
      // On success onComplete navigates away; this component unmounts.
    } catch (err) {
      console.error('Failed to start trial:', err);
      // Stay on the page so the parent can fix their slot and retry. Holds are
      // untouched on failure (completion is transactional), so re-pick re-acquires.
      consumedRef.current = false;
      const code = err?.response?.data?.code ?? null;
      if (
        code === 'SLOT_NO_LONGER_AVAILABLE' ||
        code === 'HOLD_NOT_FOUND' ||
        code === 'HOLD_SLOT_MISMATCH' ||
        code === 'MISSING_TRIAL_SLOTS'
      ) {
        setError(
          code === 'HOLD_NOT_FOUND'
            ? 'Your slot reservation expired. Please pick your time again.'
            : 'Your chosen time is no longer available. Please pick again.',
        );
      } else {
        setError('Failed to start your trial. Please try again.');
      }
      setSubmitting(false);
    }
  };

  const slot1Dates = Object.keys(slot1Groups).sort();

  return (
    <div className="flex flex-col items-center min-h-screen p-6">
      {loading ? (
        <div>Loading available schedules...</div>
      ) : (
        <div className="w-full max-w-4xl space-y-4">
          {children.map((child, childIndex) => (
            <Card key={childIndex} className="w-full relative">
              {childIndex > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-4 right-4 hover:bg-red-100"
                  onClick={() => handleRemoveChild(childIndex)}
                >
                  <X className="h-4 w-4 text-red-500" />
                  <span className="sr-only">Remove Child</span>
                </Button>
              )}

              <CardHeader className="pb-3">
                <CardTitle>Child {childIndex + 1} Details</CardTitle>
                <CardDescription>
                  Enter your child's details and pick a class time — a 30-minute assessment
                  followed straight away by a 30-minute free trial class.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Full Name</label>
                    <Input
                      value={child.name}
                      onChange={(e) => handleChildChange(childIndex, 'name', e.target.value)}
                      placeholder="Enter child's full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Year Group</label>
                    <Select
                      value={child.yearGroup}
                      onValueChange={(value) => handleChildChange(childIndex, 'yearGroup', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select year group" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEAR_GROUPS.map((year) => (
                          <SelectItem key={year} value={year}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Gender</label>
                    <Select
                      value={child.gender}
                      onValueChange={(value) => handleChildChange(childIndex, 'gender', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Date of Birth</label>
                    <Input
                      type="date"
                      value={child.dateOfBirth}
                      onChange={(e) => handleChildChange(childIndex, 'dateOfBirth', e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium">Choose your child's lesson time</h4>
                  <p className="text-sm text-gray-500">
                    Pick a date and time. Your child gets a one-hour session: a 30-minute
                    assessment, then their free trial class straight after. Only times with
                    space for the full hour are shown.
                  </p>

                  {slot1Dates.length === 0 ? (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>No trial slots available</AlertTitle>
                      <AlertDescription>
                        There are currently no class slots with space. Please contact us or check
                        back soon.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <h5 className="font-bold mb-2 text-lg">Class time</h5>
                        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Date</label>
                            <Select
                              value={child.slot1Date}
                              onValueChange={(value) =>
                                handleSlot1DateChange(childIndex, value)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select date" />
                              </SelectTrigger>
                              <SelectContent>
                                {slot1Dates.map((date) => (
                                  <SelectItem key={date} value={date}>
                                    {formatDateLabel(date)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Time</label>
                            <Select
                              value={child.slot1Time}
                              onValueChange={(value) =>
                                handleSlot1TimeChange(childIndex, value)
                              }
                              disabled={!child.slot1Date}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select time" />
                              </SelectTrigger>
                              <SelectContent>
                                {child.slot1Date &&
                                  (slot1Groups[child.slot1Date] || []).map((s) => (
                                    <SelectItem key={s.scheduleId} value={s.time}>
                                      {s.time}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {child.holdLoading && (
                          <p className="text-sm text-gray-500 mt-2">Reserving your session…</p>
                        )}
                        {child.holdError && (
                          <p className="text-sm text-red-500 mt-2">{child.holdError}</p>
                        )}
                      </div>

                      {/* Confirmation panel — replaces the old slot-2 picker. Shown once the
                          one-hour session is reserved. */}
                      {child.holdId && child.slot1Time && (
                        <div className="p-3 bg-indigo-50 rounded-lg">
                          <h5 className="font-bold mb-1 text-lg">
                            Your one-hour session — {formatDateLabel(child.slot1Date)}
                          </h5>
                          <p className="text-sm text-gray-700">
                            Assessment {child.slot1Time}–
                            {child.slot2Time || addMinutesHHmm(child.slot1Time, 30)}, then Free
                            Trial Class {child.slot2Time || addMinutesHHmm(child.slot1Time, 30)}–
                            {addMinutesHHmm(child.slot2Time || addMinutesHHmm(child.slot1Time, 30), 30)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {children.length < maxChildren && (
            <Button onClick={handleAddChild} variant="outline" className="w-full">
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Another Child
            </Button>
          )}

          <Button
            onClick={handleContinue}
            disabled={submitting || !children.every(isChildComplete)}
            className="w-full py-4 text-lg bg-indigo-600 hover:bg-indigo-700"
          >
            {submitting ? 'Setting up your trial…' : continueLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ChildDetails;
